import { useEffect, useState } from "react";
import { api, resolveMediaUrl, formatApiErrorDetail } from "@/lib/api";
import { Trash2, Upload, Plus, Loader2, Link as LinkIcon } from "lucide-react";

export default function GalleryManager() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    // Form state
    const [title, setTitle] = useState("");
    const [description, setDescription] = useState("");
    const [order, setOrder] = useState(0);
    const [url, setUrl] = useState("");
    const [file, setFile] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/gallery");
            setItems(data);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const onAdd = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setError("");
        try {
            const fd = new FormData();
            fd.append("title", title);
            fd.append("description", description);
            fd.append("order", String(order));
            if (file) fd.append("file", file);
            else if (url) fd.append("url", url);
            else {
                setError("Podaj URL obrazu lub wybierz plik.");
                setSubmitting(false);
                return;
            }
            await api.post("/gallery", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setTitle("");
            setDescription("");
            setOrder(0);
            setUrl("");
            setFile(null);
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSubmitting(false);
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Usunąć obraz?")) return;
        try {
            await api.delete(`/gallery/${id}`);
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    return (
        <div data-testid="admin-gallery" className="grid lg:grid-cols-12 gap-8">
            <form
                onSubmit={onAdd}
                className="panel-yellow p-6 lg:col-span-4 h-fit"
                data-testid="admin-gallery-form"
            >
                <div className="label-eyebrow mb-3">Dodaj zdjęcie</div>
                <Field label="Tytuł">
                    <input
                        data-testid="gallery-title-input"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        className="input"
                    />
                </Field>
                <Field label="Opis">
                    <textarea
                        data-testid="gallery-description-input"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={3}
                        className="input"
                    />
                </Field>
                <Field label="Kolejność">
                    <input
                        type="number"
                        data-testid="gallery-order-input"
                        value={order}
                        onChange={(e) => setOrder(parseInt(e.target.value || "0", 10))}
                        className="input"
                    />
                </Field>
                <Field label="URL obrazu (alternatywa do pliku)">
                    <div className="flex items-center gap-2">
                        <LinkIcon size={14} className="text-[color:var(--text-muted)]" />
                        <input
                            data-testid="gallery-url-input"
                            type="url"
                            value={url}
                            onChange={(e) => setUrl(e.target.value)}
                            placeholder="https://..."
                            className="input"
                        />
                    </div>
                </Field>
                <Field label="Lub prześlij plik">
                    <input
                        type="file"
                        accept="image/*"
                        data-testid="gallery-file-input"
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                        className="text-sm text-[color:var(--text-secondary)] file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-[color:var(--primary)] file:text-[color:var(--bg-base)] file:text-xs file:uppercase file:tracking-wider file:font-semibold"
                    />
                </Field>
                {error && (
                    <div className="text-sm text-red-400 mb-3" data-testid="gallery-error">
                        {error}
                    </div>
                )}
                <button
                    data-testid="gallery-submit-btn"
                    type="submit"
                    disabled={submitting}
                    className="btn-primary w-full mt-2 disabled:opacity-60"
                >
                    {submitting ? <Loader2 className="animate-spin" size={16} /> : <Plus size={16} />}
                    Dodaj
                </button>
                <style>{`
                    .input { width:100%; background: var(--bg-base); border:1px solid rgba(255,255,255,0.1); border-radius:6px; padding:8px 12px; color:white; font-size:14px; }
                    .input:focus { outline:none; border-color: var(--primary); }
                `}</style>
            </form>

            <div className="lg:col-span-8">
                <div className="label-eyebrow mb-4">Wszystkie zdjęcia ({items.length})</div>
                {loading ? (
                    <div className="text-[color:var(--text-secondary)]">
                        <Loader2 className="animate-spin inline" /> Ładowanie...
                    </div>
                ) : (
                    <div className="grid sm:grid-cols-2 gap-4">
                        {items.map((img) => (
                            <div
                                key={img.id}
                                data-testid={`gallery-row-${img.id}`}
                                className="panel-yellow p-3 flex gap-3"
                            >
                                <img
                                    src={resolveMediaUrl(img.url)}
                                    alt={img.title}
                                    className="w-24 h-24 object-cover rounded-md"
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="text-white font-medium truncate">
                                        {img.title || "-"}
                                    </div>
                                    <div className="text-xs text-[color:var(--text-secondary)] line-clamp-2">
                                        {img.description}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-muted)] mt-1">
                                        Kolejność: {img.order ?? 0}
                                    </div>
                                </div>
                                <button
                                    data-testid={`gallery-delete-${img.id}`}
                                    onClick={() => onDelete(img.id)}
                                    className="self-start text-red-400 hover:text-red-300 p-2"
                                    aria-label="Usuń"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block mb-4">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-[color:var(--text-secondary)] mb-1.5">
                {label}
            </span>
            {children}
        </label>
    );
}
